import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  PlusIcon,
  DocumentIcon,
  TrashIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { useDocument } from "../hooks/useDocument";
import DeleteConfirmationPopup from "./DeleteConfirmationPopup";
import AnimatedSearchBar from "./AnimatedSeachBar";
import _ from "lodash";

const ITEMS_PER_PAGE = 10;

const DOC_TYPES = [
  { value: "", label: "All Types" },
  { value: "proposal", label: "Proposals" },
  { value: "standard", label: "Standard" },
  { value: "capabilities", label: "Capabilities" },
];

const PERIOD_OPTIONS = [
  { value: "", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "custom", label: "Custom range" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "shared", label: "Shared" },
  { value: "drafted", label: "Drafted" },
  { value: "signed", label: "Signed" },
];

const SORT_OPTIONS = [
  { value: "desc", label: "Newest first" },
  { value: "asc", label: "Oldest first" },
];

const HomeScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial values from URL or defaults
  const initialStatus = searchParams.get("status") || "all";
  const initialDocType = searchParams.get("document_type") || "";
  const initialPeriod = searchParams.get("period") || "";
  const initialCreatedFrom = searchParams.get("created_from") || "";
  const initialCreatedTo = searchParams.get("created_to") || "";
  const initialSortOrder = searchParams.get("sort_order") || "desc";
  const initialSearch = searchParams.get("search") || "";
  const initialPage = parseInt(searchParams.get("page") || "0", 10);

  const [filter, setFilter] = useState(initialStatus);
  const [documentType, setDocumentType] = useState(initialDocType);
  const [period, setPeriod] = useState(initialPeriod);
  const [createdFrom, setCreatedFrom] = useState(initialCreatedFrom);
  const [createdTo, setCreatedTo] = useState(initialCreatedTo);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [page, setPage] = useState(initialPage);

  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  // Debounce search to prevent too many API calls
  const debouncedSearch = useCallback(
    _.debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  // Memoize the options object so the query key is stable
  const documentOptions = useMemo(() => {
    const filters = {
      search: searchTerm,
      status: filter,
      sort_by: "created_at",
      sort_order: sortOrder,
    };
    if (documentType) filters.document_type = documentType;
    if (period && period !== "custom") filters.period = period;
    if (period === "custom") {
      if (createdFrom) filters.created_from = createdFrom;
      if (createdTo) filters.created_to = createdTo;
    }
    return {
      page,
      limit: ITEMS_PER_PAGE,
      filters,
      exclude_types: ["invoice"],
    };
  }, [
    page,
    searchTerm,
    filter,
    sortOrder,
    documentType,
    period,
    createdFrom,
    createdTo,
  ]);

  const { allDocuments, pagination, isLoadingAll, isErrorAll, deleteDocument } =
    useDocument(null, null, documentOptions);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [
    filter,
    searchTerm,
    documentType,
    period,
    createdFrom,
    createdTo,
    sortOrder,
  ]);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (filter !== "all") params.set("status", filter);
    if (documentType) params.set("document_type", documentType);
    if (period) params.set("period", period);
    if (period === "custom" && createdFrom)
      params.set("created_from", createdFrom);
    if (period === "custom" && createdTo) params.set("created_to", createdTo);
    if (sortOrder !== "desc") params.set("sort_order", sortOrder);
    if (searchTerm) params.set("search", searchTerm);
    if (page > 0) params.set("page", page.toString());
    setSearchParams(params, { replace: true });
  }, [
    filter,
    documentType,
    period,
    createdFrom,
    createdTo,
    sortOrder,
    searchTerm,
    page,
    setSearchParams,
  ]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleDeleteClick = (document) => {
    setDocumentToDelete(document);
    setDeletePopupOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (documentToDelete) {
      try {
        await deleteDocument(
          documentToDelete._id,
          documentToDelete.document_type
        );
        setDeletePopupOpen(false);
        setDocumentToDelete(null);
      } catch (error) {
        console.error("Error deleting document:", error);
        alert("Failed to delete the document. Please try again.");
      }
    }
  };

  const handleResetFilters = () => {
    setFilter("all");
    setDocumentType("");
    setPeriod("");
    setCreatedFrom("");
    setCreatedTo("");
    setSortOrder("desc");
    setSearchTerm("");
    setPage(0);
  };

  // Check if any non-default filter is applied
  const hasActiveFilters =
    filter !== "all" ||
    documentType !== "" ||
    period !== "" ||
    sortOrder !== "desc" ||
    searchTerm !== "";

  // Dropdown helper component
  const Dropdown = ({ value, onChange, options, className = "" }) => (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none w-full px-3 py-2 pr-8 rounded-md border shadow-sm text-sm font-medium
          bg-white dark:bg-darkbg-700 border-grayteck-200 dark:border-grayteck-600
          text-grayteck-900 dark:text-grayteck-100 focus:outline-none cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-grayteck-500 dark:text-grayteck-400" />
    </div>
  );

  // Pagination controls
  const renderPaginationControls = () => (
    <div className="flex items-center justify-between mt-6 pb-4">
      <button
        onClick={() => handlePageChange(page - 1)}
        disabled={page === 0}
        className={`flex items-center px-4 py-2 rounded-md 
          ${
            page === 0
              ? "bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800"
              : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          } border border-gray-300 dark:border-gray-600`}
      >
        Previous
      </button>

      <span className="text-sm text-gray-600 dark:text-gray-400">
        Page {pagination.currentPage + 1} of {pagination.totalPages} (
        {pagination.totalItems} total)
      </span>

      <button
        onClick={() => handlePageChange(page + 1)}
        disabled={!pagination.hasMore}
        className={`flex items-center px-4 py-2 rounded-md 
          ${
            !pagination.hasMore
              ? "bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800"
              : "bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          } border border-gray-300 dark:border-gray-600`}
      >
        Next
      </button>
    </div>
  );

  if (isLoadingAll) {
    return <div className="text-center mt-8">Loading documents...</div>;
  }

  if (isErrorAll) {
    return (
      <div className="text-center mt-8 text-red-500">
        Error loading documents. Please try again later.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-grayteck-900 dark:text-grayteck-100 mb-4">
          Welcome to GT DocSign
        </h1>
        <p className="text-xl text-grayteck-700 dark:text-grayteck-300">
          Manage and sign your documents with ease
        </p>
      </div>

      {/* Filter Row */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Document Type */}
        <Dropdown
          value={documentType}
          onChange={setDocumentType}
          options={DOC_TYPES}
          className="w-32"
        />

        {/* Period */}
        <Dropdown
          value={period}
          onChange={setPeriod}
          options={PERIOD_OPTIONS}
          className="w-36"
        />

        {/* Custom date range */}
        <AnimatePresence>
          {period === "custom" && (
            <motion.div
              key="custom-dates"
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              exit={{ opacity: 0, width: 0 }}
              className="flex items-center gap-2 overflow-hidden"
            >
              <input
                type="date"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
                className="px-2 py-2 rounded-md border shadow-sm text-sm
                  bg-white dark:bg-darkbg-700 border-grayteck-200 dark:border-grayteck-600
                  text-grayteck-900 dark:text-grayteck-100 focus:outline-none"
              />
              <span className="text-grayteck-500 dark:text-grayteck-400 text-sm">
                to
              </span>
              <input
                type="date"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
                className="px-2 py-2 rounded-md border shadow-sm text-sm
                  bg-white dark:bg-darkbg-700 border-grayteck-200 dark:border-grayteck-600
                  text-grayteck-900 dark:text-grayteck-100 focus:outline-none"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sort */}
        <Dropdown
          value={sortOrder}
          onChange={setSortOrder}
          options={SORT_OPTIONS}
          className="w-36"
        />

        {/* Reset button */}
        {hasActiveFilters && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleResetFilters}
            className="flex items-center gap-1 text-sm text-grayteck-500 hover:text-grayteck-700 dark:text-grayteck-400 dark:hover:text-grayteck-200"
          >
            <XMarkIcon className="w-4 h-4" />
            Reset
          </motion.button>
        )}
      </div>

      {/* Status pills + Search + Create */}
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div className="flex flex-wrap items-center gap-4">
          {/* Status Pills */}
          <div className="flex overflow-x-auto pb-2 sm:pb-0 -mx-4 sm:mx-0 px-4 sm:px-0">
            <div className="flex border border-grayteck-200 dark:border-grayteck-700 rounded-lg divide-x divide-grayteck-200 dark:divide-grayteck-700 overflow-hidden">
              {STATUS_OPTIONS.map(({ value, label }) => (
                <motion.button
                  key={value}
                  whileHover={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleFilterChange(value)}
                  className={`px-4 py-2 whitespace-nowrap text-sm font-medium transition-colors ${
                    filter === value
                      ? "bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-900 dark:text-grayteck-100"
                      : "bg-white dark:bg-darkbg-800 text-grayteck-600 dark:text-grayteck-400 hover:bg-grayteck-50 dark:hover:bg-darkbg-700"
                  }`}
                >
                  {label}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Search Bar */}
          <AnimatedSearchBar onChange={handleSearchChange} />
        </div>

        {/* Create Button */}
        <Link
          to="/create"
          className="flex items-center justify-center bg-grayteck-600 hover:bg-grayteck-700 text-white font-bold py-2 px-4 rounded transition-colors duration-200"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          <span className="whitespace-nowrap">Create Document</span>
        </Link>
      </div>

      {/* Results count */}
      <div className="text-sm text-grayteck-600 dark:text-grayteck-400 mb-4">
        {pagination.totalItems > 0 ? (
          <span>
            Showing{" "}
            {Math.min(
              ITEMS_PER_PAGE,
              pagination.totalItems - page * ITEMS_PER_PAGE
            )}{" "}
            of {pagination.totalItems} documents
          </span>
        ) : (
          <span>No documents found</span>
        )}
      </div>

      {/* Document Grid */}
      <AnimatePresence mode="wait">
        {allDocuments.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="text-center py-12"
          >
            <p className="text-xl italic text-grayteck-600 dark:text-grayteck-400">
              {hasActiveFilters
                ? "No documents found matching your criteria."
                : "No documents found. Create a new document to get started!"}
            </p>
            {hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="mt-4 text-grayteck-600 underline hover:text-grayteck-800 dark:text-grayteck-400 dark:hover:text-grayteck-200"
              >
                Clear filters
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 gap-4 sm:gap-6"
          >
            {allDocuments.map((doc) => (
              <motion.div
                key={doc._id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white dark:bg-darkbg-800 rounded-lg shadow-md p-4 sm:p-6 overflow-hidden"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center min-w-0 flex-1">
                    <DocumentIcon className="h-6 w-6 sm:h-8 sm:w-8 text-grayteck-600 dark:text-grayteck-400 mr-3 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-grayteck-900 dark:text-grayteck-100 truncate">
                      {doc.title}
                    </h2>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleDeleteClick(doc)}
                    className="text-red-500 hover:text-red-700 transition-colors duration-200"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </motion.button>
                </div>
                <div className="max-w-full">
                  <p className="text-grayteck-700 dark:text-grayteck-300 mb-4 text-sm sm:text-base">
                    {doc.description || "No description provided"}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <div className="min-w-0 flex-1">
                    {doc.signed_by ? (
                      <Link
                        to={`/view/${doc._id}`}
                        className="text-grayteck-600 hover:text-grayteck-800 dark:text-grayteck-400 dark:hover:text-grayteck-200 font-medium text-sm sm:text-base truncate block"
                      >
                        View Document
                      </Link>
                    ) : (
                      <Link
                        to={`/edit/${doc._id}`}
                        className="text-grayteck-600 hover:text-grayteck-800 dark:text-grayteck-400 dark:hover:text-grayteck-200 font-medium text-sm sm:text-base"
                      >
                        Edit Document
                      </Link>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                    {doc.signed_by ? (
                      <span className="text-xs sm:text-sm text-blue-500">
                        Signed
                      </span>
                    ) : doc.shareable_link ? (
                      <span className="text-xs sm:text-sm text-green-500">
                        Shared
                      </span>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {allDocuments.length > 0 && renderPaginationControls()}

      <DeleteConfirmationPopup
        isOpen={deletePopupOpen}
        onClose={() => setDeletePopupOpen(false)}
        onConfirm={handleDeleteConfirm}
        documentTitle={documentToDelete?.title}
      />
    </div>
  );
};

export default HomeScreen;
